import { Response } from 'api/typegen/client/get';
import { ROUTES } from 'utils/constants/constants_routes';
import { useCallback, useEffect, useState } from 'react';
import { useErrorManager } from 'InternalLibrary';
import axiosInstance from 'utils/axiosInstance';
import useClient from 'hooks/useClient';

export const PAYLAND_SEARCHPARAM_CONTINUE = 'paylandContinue';

interface Props {
  continueScreen?: (typeof ROUTES)[keyof typeof ROUTES];
}

export const usePayment = ({ continueScreen }: Props = {}) => {
  const ROUTES_PAYLAND_ENABLED = [
    ROUTES.FIRST_LOAN_ONLINE_BANK_APPROVED,
    ROUTES.FIRST_LOAN_ONLINE_BANK_PENDING_WAITING,
  ];

  const [autoRepayEnabled, setAutoRepayEnabled] = useState<boolean | undefined>(
    undefined,
  );
  const { client } = useClient();
  const { setError } = useErrorManager();

  // Calls the Payland API to obtain a hosted payment form URL for storing a credit card
  const generatePaylandUrl = useCallback(
    async (client: Response | undefined | null) => {
      try {
        const url_ko = `${window.location.origin}${ROUTES.PAYMENT_FAILED}?${PAYLAND_SEARCHPARAM_CONTINUE}=${continueScreen}`;
        const url_ok = `${window.location.origin}${ROUTES.PAYMENT_SUCCESS}?${PAYLAND_SEARCHPARAM_CONTINUE}=${continueScreen}`;

        const response = await axiosInstance.post(
          '/cardpayments/store-credit-card',
          {
            applicationName: 'Vivus.es',
            clientNumber: client?.number,
            failureUrl: url_ko,
            successUrl: url_ok,
            username: client?.fullName,
          },
          {
            headers: {
              Accept: 'application/vnd.cardpayments.v1+json',
              'Content-Type': 'application/vnd.cardpayments.v1+json',
            },
          },
        );
        return response?.data?.payformUrl;
      } catch (e) {
        setError(e);
        return;
      }
    },
    [continueScreen, setError],
  );

  // Generates the Payland redirect URL and immediately navigates to it
  const activateAutoRepay = useCallback(async () => {
    const source = await generatePaylandUrl(client);

    if (source) {
      window.location.replace(source);
    }
  }, [client, generatePaylandUrl]);

  useEffect(() => {
    const checkUserPreferences = async () => {
      // Skip the API call if no session token is present (unauthenticated state)
      if (!sessionStorage.getItem('token')) return;

      try {
        const response = await axiosInstance.get('/client/payment-preferences');
        const { autoRepay } = response.data || {};
        setAutoRepayEnabled(autoRepay);
      } catch (e) {
        setError(e);
      }
    };
    const isPaylandRoute = ROUTES_PAYLAND_ENABLED.includes(
      window.location.pathname,
    );
    // Re-check preferences on Payland ROUTES or on first load (undefined means not yet fetched)
    if (isPaylandRoute || autoRepayEnabled === undefined)
      checkUserPreferences();
  }, [autoRepayEnabled, setError]);

  return {
    activateAutoRepay,
    autoRepayEnabled,
    showAutoPaymentSection: autoRepayEnabled !== true,
  };
};
